// @flow
import { useFormikContext } from 'formik';
import { get } from 'lodash';
import * as React from 'react';
import { IDropdownOption } from '../../../../../../components/Dropdown/IDropdown';
import { useAppSelector } from '../../../../../../reduxService/hooks';
import { IPhysicalDataResponse } from '../../../../../api/IApiResponse';
import { UnitOfMeasure } from '../../../../../enum/project';
import { selectProjectInfo } from '../../../../../store/projectInfo/projectInfoSlice';
import { selectMaterialDetail } from '../../../../selector/conversion';
import MaterialDataCheckboxes from '../MaterialDataCheckboxes/MaterialDataCheckboxes';
import MaterialDataDropdowns from '../MaterialDataDropdowns/MaterialDataDropdowns';
import { IFormikForm } from '../ModalMaterial/FormikMaterial';
import { castToIDropdownOptionArray } from './MaterialCategoryFactory';
import Natural from './Natural';

type Props = {};

export const thicknessExceptionGabion = 0.5; //Only this value required another approx
export const thicknessImperialValueGabion = 18;

export const rockD50ExceptionGabion = 0.127; //Only this value required another approx
export const rockD50ImperialValueGabion = 6.0;

const labelMajorEqual = '>=';

export const Gabion = (props: Props) => {
    const materialDetails = useAppSelector(selectMaterialDetail);
    const { data } = materialDetails;
    const { values, setFieldValue } = useFormikContext<IFormikForm>();
    const projectInfo = useAppSelector(selectProjectInfo);
    let isImperial = projectInfo?.data?.unitOfMeasure == UnitOfMeasure.Imperial;
    const [optionThickness, setOptionsThickness] = React.useState<IDropdownOption[]>([]);
    const [optionsCu, setOptionsCu] = React.useState<IDropdownOption[]>([]);
    const [optionsRock, setOptionsRock] = React.useState<IDropdownOption[]>([]);
    const handleInputChange = (e: any) => {};
    const [localThickness, setThickness] = React.useState<number>(values[values.sectorChannel].thickness || 0);
    const [cu, setCu] = React.useState<number>(values[values.sectorChannel].cu || 0);
    const [rock, setRock] = React.useState<number>(values[values.sectorChannel].rockD50 || 0);
    React.useEffect(() => {
        const physicalData = values[values.sectorChannel].material?.physicalData;
        if (physicalData) {
            let thicknessOptions = castToIDropdownOptionArray(physicalData!, 'thickness', isImperial);
            if (isImperial) {
                thicknessOptions = thicknessOptions.map(x => {
                    if (x.value === thicknessExceptionGabion) {
                        x.label = thicknessImperialValueGabion.toString();
                    }
                    return x;
                });
            }
            thicknessOptions = thicknessOptions.map(x => {
                x.label = labelMajorEqual.concat(x.label);
                return x;
            });
            setOptionsThickness(thicknessOptions);
            let firstPhysicalData = physicalData![0];
            if (!!firstPhysicalData.thickness) {
                let filteredPhysicalData = physicalData!.filter(data => firstPhysicalData.thickness == data.thickness);
                let cuOpt = castToIDropdownOptionArray(filteredPhysicalData, 'cu', isImperial);
                let rockOpt = castToIDropdownOptionArray(filteredPhysicalData, 'rockD50', isImperial);
                rockOpt = rockOpt.map(x => {
                    x.label = labelMajorEqual.concat(x.label);
                    return x;
                });
                setOptionsRock(rockOpt);
                setOptionsCu(cuOpt);
            }
        }
    }, []);
    let physicalData: IPhysicalDataResponse[] = get(values, `${values.sectorChannel}.material.physicalData`, []);

    const physicalDataCu = physicalData!.find(pd => !!pd.cu);

    React.useEffect(() => {
        if (!!physicalData && physicalData?.length > 0) {
            let thicknessOptions = castToIDropdownOptionArray(physicalData!, 'thickness', isImperial);
            let filteredPhysicalData = physicalData!.filter(data => localThickness == data.thickness);
            let rockOptions = castToIDropdownOptionArray(filteredPhysicalData, 'rockD50', isImperial);
            let cuOptions = castToIDropdownOptionArray(filteredPhysicalData, 'cu', isImperial);
            if (isImperial) {
                thicknessOptions = thicknessOptions.map(x => {
                    if (x.value === thicknessExceptionGabion) {
                        x.label = thicknessImperialValueGabion.toString();
                    }
                    return x;
                });
            }
            thicknessOptions = thicknessOptions.map(x => {
                x.label = labelMajorEqual.concat(x.label);
                return x;
            });
            rockOptions = rockOptions.map(x => {
                x.label = labelMajorEqual.concat(x.label);
                return x;
            });
            setOptionsThickness(thicknessOptions);
            setOptionsRock(rockOptions);
            setOptionsCu(cuOptions);
        }
    }, [physicalData]);

    const setValueForThickness = React.useCallback(
        (value: number) => {
            let filteredPhysicalData: IPhysicalDataResponse[] = physicalData!.filter(data => value == data.thickness);
            let rockOptions = castToIDropdownOptionArray(filteredPhysicalData, 'rockD50', isImperial);
            let cuOptions = castToIDropdownOptionArray(filteredPhysicalData, 'cu', isImperial);
            setOptionsRock(rockOptions);
            setOptionsCu(cuOptions);
            setThickness(value);
            setFieldValue(`${values.sectorChannel}.thickness`, value);
            let selectedPhysicalData = undefined;
            if (!!physicalData && physicalData[0].cu)
                selectedPhysicalData =
                    physicalData &&
                    physicalData?.find(data =>
                        !!filteredPhysicalData.length
                            ? value === data.thickness && filteredPhysicalData[0].rockD50 === data.rockD50 && filteredPhysicalData[0].cu === data.cu
                            : value === data.thickness && rock === data.rockD50 && cu === data.cu,
                    );
            else {
                selectedPhysicalData = physicalData && physicalData?.find(data => value === data.thickness && rock === data.rockD50);
            }
            if (!!selectedPhysicalData && selectedPhysicalData.id) {
                setFieldValue(`${values.sectorChannel}.combinationPD`, selectedPhysicalData.id);
                setFieldValue(`${values.sectorChannel}.allowShearStress`, selectedPhysicalData.allowShearStress);
                setFieldValue(`${values.sectorChannel}.roughness`, selectedPhysicalData.roughness);
            }
        },
        [physicalData, cu, rock],
    );

    const setValueForRock = React.useCallback(
        (value: number) => {
            let filteredPhysicalData = physicalData!.filter(data => value == data.rockD50);
            let cuOptions = castToIDropdownOptionArray(filteredPhysicalData, 'cu', isImperial);
            setOptionsCu(cuOptions);
            setRock(value);
            setFieldValue(`${values.sectorChannel}.rockD50`, value);
            let selectedPhysicalData = undefined;

            if (!!physicalData && physicalData[0].cu) selectedPhysicalData = physicalData && physicalData?.find(data => localThickness === data.thickness && value === data.rockD50 && cu === data.cu);
            else {
                selectedPhysicalData = physicalData && physicalData?.find(data => localThickness === data.thickness && value === data.rockD50);
            }
            if (cuOptions.length === 1) {
                setCu(cuOptions[0].value as number);
                if (!!physicalData && physicalData[0].cu)
                    selectedPhysicalData = physicalData && physicalData?.find(data => localThickness === data.thickness && value === data.rockD50 && cuOptions[0].value === data.cu);
            }

            if (!!selectedPhysicalData && selectedPhysicalData.id) {
                setFieldValue(`${values.sectorChannel}.combinationPD`, selectedPhysicalData.id);
                setFieldValue(`${values.sectorChannel}.allowShearStress`, selectedPhysicalData.allowShearStress);
                setFieldValue(`${values.sectorChannel}.roughness`, selectedPhysicalData.roughness);
            }
        },
        [physicalData, localThickness, cu],
    );

    const setValueForCu = React.useCallback(
        (value: number) => {
            setFieldValue(`${values.sectorChannel}.cu`, value);
            setCu(value);
            let selectedPhysicalData = undefined;
            selectedPhysicalData = physicalData && physicalData?.find(data => localThickness === data.thickness && rock === data.rockD50 && value === data.cu);

            if (!!selectedPhysicalData && selectedPhysicalData.id) {
                setFieldValue(`${values.sectorChannel}.combinationPD`, selectedPhysicalData.id);
                setFieldValue(`${values.sectorChannel}.allowShearStress`, selectedPhysicalData.allowShearStress);
                setFieldValue(`${values.sectorChannel}.roughness`, selectedPhysicalData.roughness);
            }
        },
        [physicalData, localThickness, rock],
    );
    const handleDropdownChange = (e: any) => {
        let target = e.target;
        let dropdownName = target.id;
        let value = parseFloat(target.value);
        switch (dropdownName) {
            case 'thickness':
                setValueForThickness(value);
                break;
            case 'rockD50':
                setValueForRock(value);
                break;
            default:
                setValueForCu(value);
                break;
        }
    };

    return (
        <>
            <MaterialDataCheckboxes onChange={handleInputChange} isGeotextileFilterVisible isAllowedChangeCheckbox={data?.isAllowedChangeCheckbox ? data.isAllowedChangeCheckbox : false} />
            <MaterialDataDropdowns
                onChange={handleDropdownChange}
                optionsThickness={optionThickness}
                defaultThickness={localThickness}
                optionsRock={optionsRock}
                defaultRock={rock}
                optionsCu={!!physicalDataCu ? optionsCu : undefined}
                defaultCu={cu}
            />
            <Natural hideForALl />
        </>
    );
};
